// Phase 5 service board types.
//
// Deliberately NOT in database.types.ts. That file is generated from the schema
// and gets overwritten; hand edits there vanish on the next regen. These live
// alongside it and are written by hand against migration 20260804000000.

import { formatDateShort } from './utils';

export type ServiceItemState =
    | 'unscheduled'
    | 'ready'
    | 'asap'
    | 'scheduled'
    | 'hold';

// 'completed' added 25 Aug 2026 (migration 20260825000000). Before it, bulk
// complete sent every non-billable item to 'completed_billed', so a warranty
// callback nobody was ever going to invoice read as Billed on the board. Three
// finished states now, and they mean different things: nothing to bill, still
// owed money, already invoiced.
export type ServiceItemStatus =
    | 'open'
    | 'completed'
    | 'completed_needs_billing'
    | 'completed_billed';

// Numeric 1 through 5, 1 highest. Kristine, 28 Aug 2026: "Yep 1 is high and so
// on." Replaced the low/normal/high enum rather than extending it, because
// Postgres cannot drop an enum value and the three words would have stayed in
// the type forever. A number also sorts correctly on its own: the board used to
// order this column alphabetically, High then Low then Normal.
export type ServiceItemPriority = 1 | 2 | 3 | 4 | 5;

export type CrewKind = 'pour' | 'service' | 'both';

// Ordered the way the office thinks about urgency, not alphabetically. Drives
// the default sort and the order options appear in every dropdown.
export const SERVICE_STATE_ORDER: ServiceItemState[] = [
    'asap',
    'ready',
    'scheduled',
    'unscheduled',
    'hold',
];

export const SERVICE_STATE_LABELS: Record<ServiceItemState, string> = {
    unscheduled: 'Unscheduled',
    ready: 'Ready',
    asap: 'ASAP',
    scheduled: 'Scheduled',
    hold: 'Hold',
};

export const SERVICE_STATUS_LABELS: Record<ServiceItemStatus, string> = {
    open: 'Open',
    completed: 'Completed',
    completed_needs_billing: 'Needs Billing',
    completed_billed: 'Billed',
};

// The order the status dropdown offers, matching the enum's lifecycle order
// rather than the order the union happens to be declared in.
export const SERVICE_STATUS_ORDER: ServiceItemStatus[] = [
    'open',
    'completed',
    'completed_needs_billing',
    'completed_billed',
];

// Everything that is not open. The Completed view queries on this, so adding a
// fourth finished state later means editing one array rather than hunting for
// status lists across the board, the route view and the exports.
export const SERVICE_COMPLETED_STATUSES: ServiceItemStatus[] = [
    'completed',
    'completed_needs_billing',
    'completed_billed',
];

// The order the priority dropdown offers, and the column default. 3 is the
// middle of the scale and matches the database default, so a row nobody has
// ranked reads as unranked rather than as urgent.
export const SERVICE_PRIORITY_ORDER: ServiceItemPriority[] = [1, 2, 3, 4, 5];

export const SERVICE_PRIORITY_DEFAULT: ServiceItemPriority = 3;

export const SERVICE_PRIORITY_LABELS: Record<ServiceItemPriority, string> = {
    1: '1',
    2: '2',
    3: '3',
    4: '4',
    5: '5',
};

export interface ServiceSection {
    id: string;
    name: string;
    sort_order: number;
    color_key: string;
    created_at: string;
}

export interface ServiceCommunity {
    id: string;
    builder_name: string;
    name: string;
    display_name: string;
    section_id: string | null;
    active: boolean;
    created_at: string;
    updated_at: string;
}

export interface ServiceCommunityWithSection extends ServiceCommunity {
    section: Pick<ServiceSection, 'id' | 'name' | 'sort_order' | 'color_key'> | null;
}

export interface ServiceItem {
    id: string;
    owner: string | null;
    email_date: string | null;

    community_id: string | null;
    // Preserved from the source spreadsheet. An item whose community never
    // resolved still shows the office what the sheet said, instead of a blank.
    community_raw: string | null;
    lot: string | null;
    address: string | null;
    contact: string | null;

    builder_contact: string | null;
    is_warranty: boolean;

    schedule_state: ServiceItemState;
    scheduled_date: string | null;
    // Free text the sheet carried in the SCHEDULED DATE column that is not a
    // date and not a known state: "next pour", "8/12 first stop", "due 7/17".
    schedule_note: string | null;
    priority: ServiceItemPriority;

    type_of_work: string;
    requires_concrete: boolean;
    // Overlays and floor buffing: work that eats a bigger slice of a tech's day.
    // Blue on the source sheet. Matters when someone is deciding how many stops
    // fit in a day.
    extended_time: boolean;
    notes: string | null;

    crew_id: string | null;
    crew_raw: string | null;
    backcharge_crew_id: string | null;

    status: ServiceItemStatus;
    completion_date: string | null;
    // The source column held dates AND tech names AND instructions. Anything
    // unparseable landed here rather than being dropped.
    completed_by: string | null;

    billable: boolean;
    epo_amount: number | null;
    epo_note: string | null;

    source_sheet: string | null;
    source_row: number | null;

    created_at: string;
    updated_at: string;
    created_by: string | null;
    updated_by: string | null;
}

export interface ServiceItemWithContext extends ServiceItem {
    community: {
        id: string;
        builder_name: string;
        name: string;
        display_name: string;
        section: Pick<ServiceSection, 'id' | 'name' | 'sort_order' | 'color_key'> | null;
    } | null;
    crew: { id: string; name: string } | null;
    backcharge_crew: { id: string; name: string } | null;
}

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

/**
 * What the office reads in the schedule column.
 *
 * A "scheduled" item shows its date. Everything else shows its state, and if
 * the sheet carried a note that could not be parsed into a date, that note is
 * what actually tells them what is going on, so it wins over the generic label.
 *
 * The date is formatted here rather than at each call site, so the board cell,
 * the CSV export and the column filter box all read the same. Kristine, 28 Aug.
 * Anything that needs the raw ISO value, sorting and the date input, reads
 * scheduled_date directly and must keep doing so: this returns free text on two
 * of its three branches and was never safe to parse.
 */
export function scheduleLabel(item: Pick<ServiceItem,
    'schedule_state' | 'scheduled_date' | 'schedule_note'>): string {
    if (item.schedule_state === 'scheduled' && item.scheduled_date) {
        return formatDateShort(item.scheduled_date);
    }
    if (item.schedule_note) return item.schedule_note;
    return SERVICE_STATE_LABELS[item.schedule_state];
}

/** The community label, falling back to whatever the source sheet said. */
export function communityLabel(item: ServiceItemWithContext): string {
    if (item.community) return item.community.display_name;
    if (item.community_raw) return `${item.community_raw} (unassigned)`;
    return '(no community)';
}

/** Crew label, falling back to the raw sheet value when no crew row matched. */
export function crewLabel(item: ServiceItemWithContext): string | null {
    if (item.crew) return item.crew.name;
    return item.crew_raw?.trim() || null;
}

export function isServiceItemOpen(item: Pick<ServiceItem, 'status'>): boolean {
    return item.status === 'open';
}

/** True for any finished state, whatever its billing situation. */
export function isServiceItemCompleted(item: Pick<ServiceItem, 'status'>): boolean {
    return item.status !== 'open';
}
